// AnnounceBbsInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import {
    Button, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { AppStyle, AppWord, AppObject } from '../../../value/AppConst';
import { BbsInfoDict } from '../BbsInfoDict';
import { useConfirmDialog } from '../../../common/AppConfirmDialog';
import VzmTextField from '../../../common/VzmTextField';
import VzmTextareaField from '../../../common/VzmTextareaField';
import VzmCheckbox from '../../../common/VzmCheckbox';
import VzmDateField from '../../../common/VzmDateField';
import DurationPicker from '../../../common/DurationPicker';
import { VzmInputBlock } from '../../../common/VzmStyled';
import InputUtil from '../../../value/InputUtil';
import ValueMisc from '../../../value/ValueMisc';
import { useReactiveVar } from '@apollo/client';
import { userInfoRepo } from '../../../value/AppModel';
import DraggableEditor from '../../../common/DraggableEditor';
import BbsFiles from '../BbsFiles';

const InputRow = styled(Box)({
    display: 'flex', alignItems: 'center', minWidth: 600,
});
const InputColumnBlock = styled(Box)({flexGrow:1,});

const InputMap = BbsInfoDict.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * 공지사항 게시판 입력 화면
 * @param {*} param0 
 * @returns 
 */
export default function AnnounceBbsInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
    onRequestReload
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const openConfirmDialog = useConfirmDialog();
    const sessionInfo = useReactiveVar(userInfoRepo);
    const {loading:loadingSave, error:errorSave, data:resultSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                inputSet.data.dcCd = sessionInfo.user.dscIdAs;
                inputSet.data.bbsTypeCd = BbsInfoDict.bbcType.Announce.code;
                inputSet.data.announceDate = ValueMisc.getYMD();
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item, sessionInfo]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        if(id===InputMap.popup.id && (!input.data.popupStartAt || input.data.popupStartAt==='')) {
            const startAt = ValueMisc.getYmdHms().substring(0, 14) + '00:00';
            const endDate = new Date();
            endDate.setDate(endDate.getDate() + 3);
            const endAt = ValueMisc.getYmdHms(endDate).substring(0, 14) + '00:00';
            newInput.data.popupStartAt = startAt;
            newInput.data.popupEndAt = endAt;
        }
        setInput(newInput);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    return(
        <DraggableEditor open={open} maxWidth='lg'
            onClose={onClickCancel}
            startIcon={<CampaignIcon size={AppWord.LARGE} />}
            title={isEdit ? '공지사항 수정' : '공지사항 등록'}
        >
            <DialogContent>
                <InputRow>
                    <InputColumnBlock>
                        <VzmCheckbox
                            dict={InputMap.forAll}
                            value={input.data.forAll}
                            onChange={onChangeValue}
                        />
                    </InputColumnBlock>
                    <InputColumnBlock>
                        <VzmCheckbox
                            dict={InputMap.popup}
                            value={input.data.popup}
                            onChange={onChangeValue}
                        />
                    </InputColumnBlock>
                    <InputColumnBlock>
                        <VzmCheckbox
                            dict={InputMap.topOrder}
                            value={input.data.topOrder}
                            onChange={onChangeValue}
                        />
                    </InputColumnBlock>
                </InputRow>
                {
                    input.data.popup
                    ?
                    <InputRow>
                        <DurationPicker
                            from={input.data.popupStartAt}
                            till={input.data.popupEndAt}
                            onChangeFrom={(value)=>onChangeValue(InputMap.popupStartAt.id, value, false)}
                            onChangeTill={(value)=>onChangeValue(InputMap.popupEndAt.id, value, false)}
                        />
                        <span style={{marginLeft:15}}>팝업</span>
                    </InputRow>
                    :
                    <InputRow style={{fontSize:'0.9rem', color:'#555', height:43}}>
                        <div>
                            이 공지사항은 팝업으로 표시되지 않습니다.
                            팝업창으로 표시하려면 '팝업창 표시'를 체크하세요.
                        </div>
                    </InputRow>
                }
                <VzmInputBlock>
                    <VzmTextField
                        dict={InputMap.title}
                        value={input.data.title}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                <Box style={{display: 'flex', gap: 20}}>
                    <VzmInputBlock style={{flexGrow:1}}>
                        <VzmTextField
                            dict={InputMap.email}
                            value={input.data.email}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </VzmInputBlock>
                    <VzmInputBlock>
                        <VzmDateField
                            dict={InputMap.announceDate}
                            value={input.data.announceDate}
                            error={input.error.announceDate}
                            onChange={onChangeValue}
                        />
                    </VzmInputBlock>
                </Box>
                <VzmInputBlock>
                    <VzmTextareaField
                        dict={InputMap.content}
                        value={input.data.content}
                        onChange={onChangeValue}
                        fullWidth={true}
                    />
                </VzmInputBlock>
                {
                    isEdit &&
                    <BbsFiles
                        item={item}
                        actionDisabled={input.changed}
                        onRequestReload={onRequestReload}
                    />
                }
            </DialogContent>
            <DialogActions>
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
